import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { sessionManager } from '../../config/sessionManager';
import { PermissionLevel } from '../../config/permissions';
import { Dropdown } from '../basic';
import { WelcomeModal } from '../modals';
import api from '../../config/axiosConfig';
import { API_URLS } from '../../config/Constants';
import { ROUTES } from '../../routes/routeConstants';
import { getBillingStatus, isBillingEnabled } from '../../services/billingService';

interface DropdownItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  variant?: 'default' | 'danger';
  separator?: boolean;
}

interface DropdownSection {
  title?: string;
  items: DropdownItem[];
}

interface HeaderProps {
  onLogout: () => void;
}

const Header: React.FC<HeaderProps> = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [clientName, setClientName] = useState<string>('');
  const [loadingClientName, setLoadingClientName] = useState<boolean>(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState<boolean>(false);
  const [billingEnabled, setBillingEnabled] = useState<boolean>(true);

  // Get current user data
  const currentUser = sessionManager.getUser();
  const userName = currentUser?.name || 'User';
  const isAdmin = sessionManager.isAdmin();

  // Check permissions using the new permission system
  const canAccessClients = isAdmin || sessionManager.isEmployee();
  const canAccessLeaderboards = sessionManager.isEmployee();
  const canAccessUsers = sessionManager.hasPermission('users', PermissionLevel.VIEW);
  const canAccessPlayers = sessionManager.hasPermission('players', PermissionLevel.VIEW);
  const canAccessOpponents = sessionManager.hasPermission('opponents', PermissionLevel.VIEW);
  const canAccessSeasons = sessionManager.hasPermission('seasons', PermissionLevel.VIEW);

  // Fetch client name from backend when component mounts or clientId changes
  useEffect(() => {
    const fetchClientName = async () => {
      const clientId = sessionManager.getClientId();

      if (!clientId) {
        setClientName('');
        return;
      }

      // Check if we already have client name in session
      const existingClientName = sessionManager.getClientName();
      if (existingClientName) {
        setClientName(existingClientName);
        return;
      }

      try {
        setLoadingClientName(true);
        const response = await api.get(API_URLS.GET_CLIENT_BY_ID, {
          params: { clientId },
        });

        if (response.data.success) {
          const client = response.data.data;
          const name = client.name || '';
          setClientName(name);

          // Update session manager with the fetched client name
          sessionManager.setClientName(name);
        } else {
          console.error('Failed to fetch client:', response.data.message);
          setClientName('');
        }
      } catch (error) {
        console.error('Error fetching client info:', error);
        setClientName('');
      } finally {
        setLoadingClientName(false);
      }
    };

    fetchClientName();

    // Listen for storage changes (when session is updated)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'statdog_session') {
        fetchClientName();
      }
    };

    // Listen for custom events (when session is updated programmatically)
    const handleSessionChange = () => {
      fetchClientName();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('sessionChange', handleSessionChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('sessionChange', handleSessionChange);
    };
  }, []);

  useEffect(() => {
    const loadBillingAvailability = async () => {
      if (!isClientOwner()) {
        setBillingEnabled(true);
        return;
      }

      const response = await getBillingStatus();
      if (response.success) {
        setBillingEnabled(isBillingEnabled(response.billing));
      }
    };

    loadBillingAvailability();
  }, []);

  const handleLogoutClick = () => {
    onLogout();
  };

  const handleProfileClick = () => {
    navigate('/profile');
  };

  const handleAccountClick = () => {
    navigate('/account');
  };

  const handleSupportClick = () => {
    // Navigate to Help Center
    navigate(ROUTES.HELP_CENTER);
  };

  const handleCoachCornerClick = () => {
    // Navigate to Coach Corner
    navigate(ROUTES.COACH_CORNER);
  };

  const handleSetUpWizardClick = () => {
    setShowWelcomeModal(true);
  };

  const isClientOwner = () => {
    // Head coaches and assistant coaches are always client owners
    if (sessionManager.isHeadCoach() || sessionManager.isAssistantCoach()) {
      return true;
    }

    return false;
  };

  // Check if user should always show client name (common users with clientId)
  const shouldShowClientName = () => {
    const clientId = sessionManager.getClientId();
    return !!clientId;
  };

  // Create dropdown sections
  const createDropdownSections = (): DropdownSection[] => {
    const sections: DropdownSection[] = [];

    // Profile section - available to ALL users (common users and employees)
    sections.push({
      title: 'Profile',
      items: [
        {
          id: 'profile',
          label: 'Manage Profile',
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
          ),
          onClick: handleProfileClick,
        },
        {
          id: 'setupWizard',
          label: 'Set Up Wizard',
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          ),
          onClick: handleSetUpWizardClick,
        },
      ],
    });

    // Account section - only for Client Owners (Head Coach, Assistant Coach, Admin)
    if (isClientOwner()) {
      sections.push({
        title: 'Account',
        items: [
          {
            id: 'account',
            label: 'Team Info',
            icon: (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
            ),
            onClick: handleAccountClick,
          },
        ],
      });
    }

    // Renewals & Billing section - only for Head Coach and Assistant Coach
    if (isClientOwner() && billingEnabled) {
      sections.push({
        title: 'Billing',
        items: [
          {
            id: 'billing',
            label: 'Billing and Renewals',
            icon: (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                />
              </svg>
            ),
            onClick: () => navigate('/billing'),
          },
        ],
      });
    }

    // Support section - available to ALL users
    sections.push({
      title: 'Support',
      items: [
        {
          id: 'coachCorner',
          label: "Coach's Corner",
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
              />
            </svg>
          ),
          onClick: handleCoachCornerClick,
        },
        {
          id: 'support',
          label: 'Help & Support',
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          ),
          onClick: handleSupportClick,
        },
      ],
    });

    // Logout section - available to ALL users
    sections.push({
      items: [
        {
          id: 'logout',
          label: 'Sign Out',
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
          ),
          onClick: handleLogoutClick,
          variant: 'danger',
          separator: true,
        },
      ],
    });

    return sections;
  };

  // Create account dropdown trigger
  const createAccountTrigger = () => (
    <div className="flex flex-col items-end">
      <div className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors duration-150">
        <div className="text-right">
          <div className="font-semibold text-sm text-gray-900">{userName}</div>
          {shouldShowClientName() && (
            <div className="text-xs text-gray-500">
              {loadingClientName ? 'Loading organization...' : clientName || 'Organization'}
            </div>
          )}
        </div>
        <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full">
          <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
          </svg>
        </div>
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  );

  // Create mobile account trigger (simpler)
  const createMobileAccountTrigger = () => (
    <div className="flex items-center space-x-2">
      <div className="text-right">
        <div className="font-semibold text-sm text-gray-900">{userName}</div>
        {shouldShowClientName() && (
          <div className="text-xs text-gray-500">
            {loadingClientName ? 'Loading organization...' : clientName || 'Organization'}
          </div>
        )}
      </div>
      <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full">
        <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          />
        </svg>
      </div>
      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </div>
  );

  const handleLogoClick = () => {
    if (sessionManager.isEmployee()) {
      navigate('/dashboard');
    } else if (canAccessSeasons) {
      navigate('/seasons'); // Default for coaches and most client users
    } else {
      navigate('/games'); // Fallback for stat takers and other users
    }
  };

  return (
    <header className="w-full min-w-0 max-w-[100vw] flex flex-col items-center bg-white shadow">
      <div className="w-full min-w-0 flex flex-col items-center">
        <div className="flex items-center w-full p-3 sm:px-7 justify-between md:hidden">
          <div className="flex items-center">
            <button onClick={handleLogoClick}>
              <img src="/images/logo.png" alt="Logo" className="w-40 sm:w-48 h-10 sm:h-12 mr-2" />
            </button>
          </div>

          {/* Mobile Account Dropdown */}
          <Dropdown trigger={createMobileAccountTrigger()} sections={createDropdownSections()} align="right" />
        </div>
        <div
          className={`relative h-[50px] md:h-[100px] bg-[#e8f2fc] md:bg-white w-full min-w-0 flex flex-row items-center justify-between ${
            location.pathname.startsWith('/clients') ||
            location.pathname.startsWith('/leaderboards') ||
            location.pathname.startsWith('/users') ||
            location.pathname.startsWith('/players') ||
            location.pathname.startsWith('/opponents')
              ? 'md:!justify-between'
              : 'md:justify-between'
          } py-3 px-1 sm:px-7`}
        >
          {/* Left: Logo (desktop only) */}
          <div className="hidden md:flex items-center mr-4">
            <button onClick={handleLogoClick} className="flex items-center group">
              <img
                src="/images/logo.png"
                alt="Logo"
                className="h-10 sm:h-20 w-auto object-contain transition-transform group-hover:scale-105"
              />
            </button>
          </div>

          {/* Right side container */}
          <div className="flex items-center gap-6 w-full min-w-0">
            {/* Mobile simplified tabs row: ALL route links in one horizontal row */}
            <div className="flex md:hidden w-full min-w-0 overflow-x-auto scrollbar-hide overscroll-x-contain [-webkit-overflow-scrolling:touch]">
              <ul className="flex w-max min-w-full justify-start space-x-3 px-1 text-sm font-semibold tracking-wide whitespace-nowrap">
                <li>
                  <NavLink
                    to="/games"
                    className={() =>
                      `${
                        location.pathname === '/games' || location.pathname.startsWith('/games/')
                          ? 'text-[#19133c] underline font-bold underline-offset-4 decoration-[#7fe526]'
                          : 'text-[#19133c]/60'
                      }`
                    }
                  >
                    Games
                  </NavLink>
                </li>
                {canAccessSeasons && (
                  <li>
                    <NavLink
                      to="/seasons"
                      className={() =>
                        `${
                          location.pathname === '/seasons' || location.pathname.startsWith('/seasons/')
                            ? 'text-[#19133c] underline font-bold underline-offset-4 decoration-[#7fe526]'
                            : 'text-[#19133c]/60'
                        }`
                      }
                    >
                      Seasons
                    </NavLink>
                  </li>
                )}
                {canAccessClients && (
                  <li>
                    <NavLink
                      to="/clients"
                      className={() =>
                        `${
                          location.pathname.startsWith('/clients')
                            ? 'text-[#19133c] underline font-bold underline-offset-4 decoration-[#7fe526]'
                            : 'text-[#19133c]/60'
                        }`
                      }
                    >
                      Clients
                    </NavLink>
                  </li>
                )}
                {canAccessLeaderboards && (
                  <li>
                    <NavLink
                      to="/leaderboards"
                      className={() =>
                        `${
                          location.pathname.startsWith('/leaderboards')
                            ? 'text-[#19133c] underline font-bold underline-offset-4 decoration-[#7fe526]'
                            : 'text-[#19133c]/60'
                        }`
                      }
                    >
                      Leaderboards
                    </NavLink>
                  </li>
                )}
                {canAccessUsers && (
                  <li>
                    <NavLink
                      to="/users"
                      className={() =>
                        `${
                          location.pathname.startsWith('/users')
                            ? 'text-[#19133c] underline font-bold underline-offset-4 decoration-[#7fe526]'
                            : 'text-[#19133c]/60'
                        }`
                      }
                    >
                      Users
                    </NavLink>
                  </li>
                )}
                {canAccessPlayers && (
                  <li>
                    <NavLink
                      to="/players"
                      className={() =>
                        `${
                          location.pathname.startsWith('/players')
                            ? 'text-[#19133c] underline font-bold underline-offset-4 decoration-[#7fe526]'
                            : 'text-[#19133c]/60'
                        }`
                      }
                    >
                      Players
                    </NavLink>
                  </li>
                )}
                {canAccessOpponents && (
                  <li>
                    <NavLink
                      to="/opponents"
                      className={() =>
                        `${
                          location.pathname.startsWith('/opponents')
                            ? 'text-[#19133c] underline font-bold underline-offset-4 decoration-[#7fe526]'
                            : 'text-[#19133c]/60'
                        }`
                      }
                    >
                      Opponents
                    </NavLink>
                  </li>
                )}
              </ul>
            </div>

            {/* Navigation Tabs (desktop) */}
            <div className="flex-1 hidden md:flex justify-end">
              <ul className="flex space-x-2 xss:space-x-3 md:space-x-2 lg:space-x-4 text-base md:text-sm lg-text-[18px] xl:text-xl text-black bg-[#e8f2fc] p-2 xss:p-3 rounded-2xl">
                <li>
                  <NavLink
                    to="/games"
                    className={() =>
                      `relative ${
                        location.pathname === '/games' || location.pathname.startsWith('/games/')
                          ? "font-bold after:content-[''] after:block after:h-0.5 after:bg-[#7fe526] after:mt-2 after:rounded after:w-full after:absolute after:left-0 after:-bottom-1"
                          : 'font-semibold'
                      }`
                    }
                  >
                    Games
                  </NavLink>
                </li>
                {canAccessSeasons && (
                  <li>
                    <NavLink
                      to="/seasons"
                      className={() =>
                        `relative ${
                          location.pathname === '/seasons' || location.pathname.startsWith('/seasons/')
                            ? "font-bold after:content-[''] after:block after:h-0.5 after:bg-[#7fe526] after:mt-2 after:rounded after:w-full after:absolute after:left-0 after:-bottom-1"
                            : 'font-semibold'
                        }`
                      }
                    >
                      Seasons
                    </NavLink>
                  </li>
                )}
                <div className="space-x-2 xss:space-x-3 md:space-x-2 hidden md:flex">
                  {canAccessClients && (
                    <li>
                      <NavLink
                        to="/clients"
                        className={({ isActive }) =>
                          `relative ${
                            isActive
                              ? "font-bold after:content-[''] after:block after:h-0.5 after:bg-[#7fe526] after:mt-2 after:rounded after:w-full after:absolute after:left-0 after:-bottom-1"
                              : 'font-semibold'
                          }`
                        }
                      >
                        Clients
                      </NavLink>
                    </li>
                  )}
                  {canAccessLeaderboards && (
                    <li>
                      <NavLink
                        to="/leaderboards"
                        className={({ isActive }) =>
                          `relative ${
                            isActive
                              ? "font-bold after:content-[''] after:block after:h-0.5 after:bg-[#7fe526] after:mt-2 after:rounded after:w-full after:absolute after:left-0 after:-bottom-1"
                              : 'font-semibold'
                          }`
                        }
                      >
                        Leaderboards
                      </NavLink>
                    </li>
                  )}
                  {canAccessUsers && (
                    <li>
                      <NavLink
                        to="/users"
                        className={({ isActive }) =>
                          `relative ${
                            isActive
                              ? "font-bold after:content-[''] after:block after:h-0.5 after:bg-[#7fe526] after:mt-2 after:rounded after:w-full after:absolute after:left-0 after:-bottom-1"
                              : 'font-semibold'
                          }`
                        }
                      >
                        Users
                      </NavLink>
                    </li>
                  )}
                  {canAccessPlayers && (
                    <li>
                      <NavLink
                        to="/players"
                        className={({ isActive }) =>
                          `relative ${
                            isActive
                              ? "font-bold after:content-[''] after:block after:h-0.5 after:bg-[#7fe526] after:mt-2 after:rounded after:w-full after:absolute after:left-0 after:-bottom-1"
                              : 'font-semibold'
                          }`
                        }
                      >
                        Players
                      </NavLink>
                    </li>
                  )}
                  {canAccessOpponents && (
                    <li>
                      <NavLink
                        to="/opponents"
                        className={({ isActive }) =>
                          `relative ${
                            isActive
                              ? "font-bold after:content-[''] after:block after:h-0.5 after:bg-[#7fe526] after:mt-2 after:rounded after:w-full after:absolute after:left-0 after:-bottom-1"
                              : 'font-semibold'
                          }`
                        }
                      >
                        Opponents
                      </NavLink>
                    </li>
                  )}
                </div>
              </ul>
            </div>

            {/* Account dropdown (desktop) */}
            <div className="hidden md:block">
              <Dropdown trigger={createAccountTrigger()} sections={createDropdownSections()} align="right" />
            </div>
          </div>
        </div>
      </div>

      {/* Welcome Modal */}
      <WelcomeModal isOpen={showWelcomeModal} onClose={() => setShowWelcomeModal(false)} clientName={clientName} />
    </header>
  );
};

export default Header;
